(function () {
  'use strict';

  var SERVICE_VARIANT_ID = 52318120706349;
  var SERVICE_FEE_CENTS = 3000;
  var requirementCache = new Map();
  var reconcilePromise = null;
  var checkoutRunning = false;
  var drawerInstallTimer = null;
  var productSyncTimers = new WeakMap();

  function readConfig() {
    var element = document.getElementById('hok-cart-integrity-config');
    if (!element) return {};
    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  var config = readConfig();
  var errorMessage = String(config.errorMessage || '');
  var updatingMessage = String(config.updatingMessage || '');

  function normaliseBoolean(value) {
    if (value === true || value === false) return value;
    var text = String(value == null ? '' : value).trim().toLowerCase();
    if (text === 'true' || text === '1' || text === 'yes') return true;
    if (text === 'false' || text === '0' || text === 'no') return false;
    return null;
  }

  function money(cents) {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(Number(cents || 0) / 100);
  }

  async function fetchCart() {
    var response = await fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('CART_FETCH_FAILED');
    return response.json();
  }

  function getExplicitRequirement(item) {
    if (!item || !item.properties) return null;
    if (!Object.prototype.hasOwnProperty.call(item.properties, '_hok_tailoring_required')) return null;
    return normaliseBoolean(item.properties._hok_tailoring_required);
  }

  async function getRequirement(item) {
    if (!item || Number(item.variant_id) === SERVICE_VARIANT_ID) return false;
    var explicit = getExplicitRequirement(item);
    var variantId = String(item.variant_id || item.id || '');
    if (!variantId) return explicit;
    if (requirementCache.has(variantId)) return requirementCache.get(variantId);
    var productUrl = item.url || (item.handle ? '/products/' + encodeURIComponent(item.handle) + '?variant=' + variantId : '');
    if (!productUrl) return explicit;
    try {
      var response = await fetch(productUrl, {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        cache: 'no-store'
      });
      if (!response.ok) return explicit;
      var html = await response.text();
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var data = parsed.querySelector('.hok-tailoring-json');
      if (!data) return explicit;
      var map = JSON.parse(data.textContent || '{}');
      var required = map[variantId] === true;
      requirementCache.set(variantId, required);
      return required;
    } catch (error) {
      return explicit;
    }
  }

  async function calculateRequiredQuantity(cart) {
    var garments = (cart.items || []).filter(function (item) {
      return Number(item.variant_id) !== SERVICE_VARIANT_ID;
    });
    var requirements = await Promise.all(garments.map(getRequirement));
    if (requirements.some(function (value) { return value === null; })) return null;
    return garments.reduce(function (total, item, index) {
      return total + (requirements[index] ? Number(item.quantity || 0) : 0);
    }, 0);
  }

  function getServiceQuantity(cart) {
    return (cart.items || []).reduce(function (total, item) {
      return total + (Number(item.variant_id) === SERVICE_VARIANT_ID ? Number(item.quantity || 0) : 0);
    }, 0);
  }

  function serviceProperties(cart) {
    var labels = (cart.items || []).filter(function (item) {
      return Number(item.variant_id) !== SERVICE_VARIANT_ID && cachedRequirement(item);
    }).map(function (item) {
      var size = String(item.variant_title || '').replace(/^AU\s*\d+\s*\/\s*Bust\s*:\s*(\d+)"?\s*\/\s*(\S+)$/i, '$2 · Bust $1"');
      return item.product_title + (size ? ' — ' + size : '') + ' (qty ' + item.quantity + ')';
    });
    return { _hok_tailoring_service: 'true', 'Applies to': labels.join('; ') };
  }

  function serviceDetailsMatch(cart) {
    var items = (cart.items || []).filter(function (item) { return Number(item.variant_id) === SERVICE_VARIANT_ID; });
    if (!items.length) return true;
    if (items.length !== 1) return false;
    var actual = items[0].properties || {};
    var desired = serviceProperties(cart);
    return Object.keys(actual).length === Object.keys(desired).length && Object.keys(desired).every(function (key) { return actual[key] === desired[key]; });
  }

  async function writeServiceQuantity(cart, requiredQuantity) {
    var serviceItems = (cart.items || []).filter(function (item) {
      return Number(item.variant_id) === SERVICE_VARIANT_ID;
    });
    if (!serviceItems.length && requiredQuantity > 0) {
      var addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: SERVICE_VARIANT_ID,
            quantity: requiredQuantity,
            properties: serviceProperties(cart)
          }]
        })
      });
      if (!addResponse.ok) throw new Error('SERVICE_ADD_FAILED');
      return fetchCart();
    }
    if (!serviceItems.length) return cart;
    var updates = {};
    serviceItems.forEach(function (item, index) {
      updates[item.key] = index === 0 ? requiredQuantity : 0;
    });
    var updateResponse = await fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    });
    if (!updateResponse.ok) throw new Error('SERVICE_UPDATE_FAILED');
    var updated = await updateResponse.json();
    if (requiredQuantity > 0) {
      var detailsResponse = await fetch('/cart/change.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: serviceItems[0].key, quantity: requiredQuantity, properties: serviceProperties(cart) })
      });
      if (!detailsResponse.ok) throw new Error('SERVICE_DETAILS_FAILED');
      updated = await detailsResponse.json();
    }
    return updated;
  }

  function reconcileCart(cart) {
    if (reconcilePromise) return reconcilePromise;
    reconcilePromise = (async function () {
      var current = cart && Array.isArray(cart.items) ? cart : await fetchCart();
      var requiredQuantity = await calculateRequiredQuantity(current);
      if (requiredQuantity === null) throw new Error('REQUIREMENT_UNRESOLVED');
      var changed = getServiceQuantity(current) !== requiredQuantity || !serviceDetailsMatch(current);
      if (changed) current = await writeServiceQuantity(current, requiredQuantity);
      var verified = await fetchCart();
      var verifiedRequired = await calculateRequiredQuantity(verified);
      if (verifiedRequired === null || getServiceQuantity(verified) !== verifiedRequired) {
        throw new Error('SERVICE_VERIFICATION_FAILED');
      }
      return { cart: verified, changed: changed, requiredQuantity: verifiedRequired };
    })().finally(function () {
      reconcilePromise = null;
    });
    return reconcilePromise;
  }

  function parseProductData(root) {
    var element = root.querySelector('.product-json');
    if (!element) return null;
    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  function parseTailoringMap(root) {
    var element = root.querySelector('.hok-tailoring-json');
    if (!element) return {};
    try {
      return JSON.parse(element.textContent || '{}');
    } catch (error) {
      return {};
    }
  }

  function ensureProperty(form, name, value) {
    var input = form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function removeProperty(form, name) {
    var input = form.querySelector('input[name="' + name + '"]');
    if (input) input.remove();
  }

  function getProductOptionSelects(root, idSelect) {
    return Array.prototype.slice.call(
      root.querySelectorAll('.wetheme-dropdown__select, .selector-wrapper select, .product-option-select select')
    ).filter(function (select, index, selects) {
      return select !== idSelect && selects.indexOf(select) === index;
    });
  }

  function syncProductVariant(root) {
    if (!root) return null;
    var form = root.querySelector('form[action*="/cart/add"]');
    var idSelect = form ? form.querySelector('select[name="id"]') : null;
    var product = parseProductData(root);
    if (!form || !idSelect || !product) return null;
    var optionSelects = getProductOptionSelects(root, idSelect);
    var selectedValues = optionSelects.map(function (select) { return select.value; });
    var variant = (product.variants || []).find(function (candidate) {
      var options = candidate.options || [];
      return options.length === selectedValues.length && options.every(function (value, index) {
        return value === selectedValues[index];
      });
    });
    if (!variant) {
      variant = (product.variants || []).find(function (candidate) {
        return String(candidate.id) === String(idSelect.value || '');
      }) || null;
    }
    if (!variant) return null;
    if (String(idSelect.value || '') !== String(variant.id)) idSelect.value = String(variant.id);
    idSelect.value = String(variant.id);
    root.setAttribute('data-selected-variant', String(variant.id));
    var tailoringMap = parseTailoringMap(root);
    var required = tailoringMap[String(variant.id)] === true;
    ensureProperty(form, 'properties[_hok_tailoring_required]', required ? 'true' : 'false');
    if (required) ensureProperty(form, 'properties[Tailoring]', 'Included +$30');
    else removeProperty(form, 'properties[Tailoring]');
    root.classList.toggle('hok-tailoring-required', required);
    var notice = root.querySelector('[data-hok-tailoring-service-notice]');
    if (notice) {
      notice.hidden = !required;
      notice.classList.toggle('is-visible', required);
    }
    return variant;
  }

  function scheduleProductVariantSync(root) {
    if (!root) return;
    var timers = productSyncTimers.get(root) || [];
    timers.forEach(function (timer) {
      window.clearTimeout(timer);
    });
    timers = [0, 60, 180].map(function (delay) {
      return window.setTimeout(function () {
        syncProductVariant(root);
      }, delay);
    });
    productSyncTimers.set(root, timers);
  }

  function installProductVariantController(root) {
    if (!root || root.dataset.hokVariantIntegrityReady === 'true') return;
    var form = root.querySelector('form[action*="/cart/add"]');
    var idSelect = form ? form.querySelector('select[name="id"]') : null;
    if (!form || !idSelect) return;
    var optionSelects = getProductOptionSelects(root, idSelect);
    optionSelects.concat([idSelect]).forEach(function (select) {
      select.addEventListener('input', function () {
        scheduleProductVariantSync(root);
      });
      select.addEventListener('change', function () {
        scheduleProductVariantSync(root);
      });
    });
    root.dataset.hokVariantIntegrityReady = 'true';
    scheduleProductVariantSync(root);
  }

  function syncAllProductVariants() {
    document.querySelectorAll('#product-box, [data-wetheme-section-type="product--featured-product"]').forEach(function (root) {
      installProductVariantController(root);
      syncProductVariant(root);
    });
  }

  function captureDrawer() {
    var drawer = document.querySelector('#cartSlideoutWrapper');
    if (!drawer) return null;
    return {
      items: drawer.querySelector('.cart-items') ? drawer.querySelector('.cart-items').innerHTML : '',
      empty: drawer.querySelector('.cart-empty-box') ? drawer.querySelector('.cart-empty-box').className : '',
      total: drawer.querySelector('.js-cart-total') ? drawer.querySelector('.js-cart-total').innerHTML : ''
    };
  }

  function restoreDrawer(snapshot) {
    if (!snapshot) return;
    var drawer = document.querySelector('#cartSlideoutWrapper');
    if (!drawer) return;
    var items = drawer.querySelector('.cart-items');
    var empty = drawer.querySelector('.cart-empty-box');
    var total = drawer.querySelector('.js-cart-total');
    if (items) items.innerHTML = snapshot.items;
    if (empty && snapshot.empty) empty.className = snapshot.empty;
    if (total) total.innerHTML = snapshot.total;
  }

  function setCartError(message) {
    var box = document.querySelector('#cartSlideoutWrapper .cart-error-box, #cartform .cart-error-box');
    if (!box) return;
    box.textContent = message || '';
    box.classList.toggle('hide', !message);
  }

  function setCheckoutBusy(busy) {
    document.querySelectorAll('#cartSlideoutWrapper [name="checkout"], #cartform [name="checkout"]').forEach(function (button) {
      var text = button.querySelector('.cart-button-checkout-text');
      if (busy) {
        if (text && !button.dataset.hokOriginalText) button.dataset.hokOriginalText = text.textContent;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (text && updatingMessage) text.textContent = updatingMessage;
      } else {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (text && button.dataset.hokOriginalText) text.textContent = button.dataset.hokOriginalText;
      }
    });
  }

  function cachedRequirement(item) {
    var explicit = getExplicitRequirement(item);
    var variantId = String(item.variant_id || item.id || '');
    if (requirementCache.has(variantId)) return requirementCache.get(variantId) === true;
    return explicit === true;
  }

  function updateHeaderCartCount(cart) {
    var count = (cart.items || []).reduce(function(total,item) {
      return total + (Number(item.variant_id) === SERVICE_VARIANT_ID ? 0 : Number(item.quantity || 0));
    }, 0);
    document.querySelectorAll('.hok-cart-count-badge').forEach(function(el) {
      el.textContent = String(count);
      el.hidden = count === 0;
      el.setAttribute('aria-label',count + (count === 1 ? ' item in cart' : ' items in cart'));
    });
  }

  function renderStableCart(cart) {
    var drawer = document.querySelector('#cartSlideoutWrapper');
    updateHeaderCartCount(cart);
    if (!drawer) return;
    var rows = Array.prototype.slice.call(drawer.querySelectorAll('.cart-items > li'));
    var items = Array.isArray(cart.items) ? cart.items : [];
    var serviceItems = items.filter(function (item) {
      return Number(item.variant_id) === SERVICE_VARIANT_ID;
    });
    var serviceQuantity = serviceItems.reduce(function (total, item) {
      return total + Number(item.quantity || 0);
    }, 0);
    var serviceFinalTotal = serviceItems.reduce(function (total, item) {
      return total + Number(item.final_line_price || 0);
    }, 0);
    var serviceOriginalTotal = serviceItems.reduce(function (total, item) {
      return total + Number(item.original_line_price || 0);
    }, 0);
    var serviceUnit = serviceQuantity > 0
      ? serviceFinalTotal / serviceQuantity
      : SERVICE_FEE_CENTS;
    var serviceOriginalUnit = serviceQuantity > 0
      ? serviceOriginalTotal / serviceQuantity
      : SERVICE_FEE_CENTS;
    var garmentCount = 0;
    rows.forEach(function (row, index) {
      var item = items[index];
      if (!item) {
        row.hidden = true;
        return;
      }
      row.dataset.hokCartKey = item.key || '';
      if (Number(item.variant_id) === SERVICE_VARIANT_ID) {
        row.hidden = true;
        row.style.display = 'none';
        row.setAttribute('aria-hidden', 'true');
        return;
      }
      garmentCount += Number(item.quantity || 0);
      row.hidden = false;
      row.style.display = '';
      row.removeAttribute('aria-hidden');
      var article = row.querySelector('.cart-item') || row;
      var image = article.querySelector('.cart-item-image');
      var imageUrl = item.image || (item.featured_image && item.featured_image.url) || '';
      if (image && imageUrl) {
        image.src = imageUrl;
        image.alt = item.product_title || item.title || '';
        image.classList.remove('d-none');
      }
      var required = cachedRequirement(item);
      var price = article.querySelector('.cart-item-price');
      var originalPrice = article.querySelector('.cart-item-price-original');
      var meta = article.querySelector('.hok-tailoring-cart-meta');
      var finalLine = Number(item.final_line_price || item.original_line_price || 0);
      var originalLine = Number(item.original_line_price || finalLine);
      if (required) {
        finalLine += serviceUnit * Number(item.quantity || 0);
        originalLine += serviceOriginalUnit * Number(item.quantity || 0);
        if (!meta) {
          meta = document.createElement('div');
          meta.className = 'hok-tailoring-cart-meta';
          meta.remove();
          var title = article.querySelector('.cart-item__title');
          if (title) title.insertAdjacentElement('afterend', meta);
        }
      } else if (meta) {
        meta.remove();
      }
      if (price) price.textContent = money(finalLine);
      if (originalPrice) {
        var showOriginal = originalLine > finalLine;
        originalPrice.textContent = showOriginal ? money(originalLine) : '';
        originalPrice.classList.toggle('hide', !showOriginal);
        originalPrice.classList.toggle('d-none', !showOriginal);
      }
      var discountMoney = article.querySelector('.order-discount--cart-amount .money');
      if (discountMoney) {
        discountMoney.textContent = money(Math.max(0, originalLine - finalLine));
      }
    });
    document.querySelectorAll('.hok-cart-count-badge').forEach(function (element) {
      element.textContent = String(garmentCount);
      element.hidden = garmentCount < 1;
      element.setAttribute('aria-label', garmentCount + (garmentCount === 1 ? ' item in cart' : ' items in cart'));
    });
    drawer.querySelectorAll('.cart-item-count-header--quantity').forEach(function (element) {
      element.textContent = String(garmentCount);
      element.hidden = garmentCount < 1;
    });
    drawer.querySelectorAll('.js-cart-total').forEach(function (element) {
      element.textContent = money(cart.total_price);
    });
    var empty = drawer.querySelector('.cart-empty-box');
    if (empty) empty.classList.toggle('hide', garmentCount > 0);
    var accelerated = drawer.querySelector('.additional-checkout-buttons');
    if (accelerated) accelerated.hidden = serviceItems.length > 0;
    document.dispatchEvent(new CustomEvent('hok:cart-rendered'));
  }

  var cartEditQueue = Promise.resolve();
  function editCartRow(row, quantity, delta) {
    var key = row && row.dataset.hokCartKey;
    var link = row && row.querySelector('a.cart-item-link[href]');
    var variantId = link && new URL(link.href, window.location.origin).searchParams.get('variant');
    cartEditQueue = cartEditQueue.catch(function () {}).then(async function () {
      setCheckoutBusy(true);
      try {
        var cart = await fetchCart();
        var item = cart.items.find(function (entry) { return entry.key === key && (!variantId || String(entry.variant_id) === variantId); });
        if (!item && variantId) {
          var matches = cart.items.filter(function (entry) { return String(entry.variant_id) === variantId; });
          if (matches.length === 1) item = matches[0];
        }
        if (!item) return;
        key = item.key;
        var next = delta == null ? quantity : Number(item.quantity) + delta;
        if (!Number.isInteger(next) || next < 0) throw new Error('INVALID_QUANTITY');
        var response = await fetch('/cart/change.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: next })
        });
        if (!response.ok) {
          var failure = await response.json().catch(function () { return {}; });
          var editError = new Error('CART_EDIT_FAILED');
          editError.customerMessage = typeof failure.description === 'string' ? failure.description : '';
          throw editError;
        }
        var result = await reconcileCart(await response.json());
        await window.wetheme.cartDrawer.updateCartDrawer(result.cart);
        setCartError('');
      } catch (error) {
        setCartError(error.customerMessage || errorMessage || 'We could not update this item. Please try again.');
      } finally { setCheckoutBusy(false); }
    });
  }
  document.addEventListener('click', function (event) {
    var button = event.target.closest('#cart-item-remove-button, .cart-item-quantity-button');
    var row = button && button.closest('#cartSlideoutWrapper .cart-items > li');
    if (!row) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var removing = button.id === 'cart-item-remove-button';
    editCartRow(row, removing ? 0 : null, removing ? null : Number(button.dataset.amount));
  }, true);
  document.addEventListener('change', function (event) {
    var input = event.target;
    var row = input.closest && input.closest('#cartSlideoutWrapper .cart-items > li');
    if (!row || !input.matches('input')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    editCartRow(row, Number(input.value), null);
  }, true);

  function refreshBadge() { fetchCart().then(updateHeaderCartCount).catch(function(){}); }
  document.addEventListener('cart:updated', refreshBadge);
  document.addEventListener('cart:refresh', refreshBadge);
  document.addEventListener('shopify:section:load', refreshBadge);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refreshBadge);
  else refreshBadge();

  function installDrawerController() {
    var drawer = window.wetheme && window.wetheme.cartDrawer;
    if (!drawer || typeof drawer.updateCartDrawer !== 'function') return false;
    if (drawer.updateCartDrawer.hokIntegrityV3) return true;
    var original = drawer.updateCartDrawer.bind(drawer);
    var running = false;
    var pendingCart = null;
    var pendingResolvers = [];

    async function drain() {
      if (running) return;
      running = true;
      while (pendingCart) {
        var next = pendingCart;
        pendingCart = null;
        var snapshot = captureDrawer();
        try {
          var result = await reconcileCart(next);
          await original(result.cart);
          renderStableCart(result.cart);
          setCartError('');
        } catch (error) {
          restoreDrawer(snapshot);
          setCartError(errorMessage);
          console.error('HoK cart integrity render failed.', error);
        }
      }
      running = false;
      var resolvers = pendingResolvers.splice(0);
      resolvers.forEach(function (resolve) { resolve(); });
    }

    function update(cart) {
      pendingCart = cart;
      var promise = new Promise(function (resolve) {
        pendingResolvers.push(resolve);
      });
      drain();
      return promise;
    }

    update.hokIntegrityV3 = true;
    drawer.updateCartDrawer = update;
    fetchCart().then(update).catch(function (error) {
      console.error('HoK cart integrity initial refresh failed.', error);
    });
    return true;
  }

  function scheduleDrawerInstall() {
    window.clearTimeout(drawerInstallTimer);
    var attempts = 0;
    function attempt() {
      attempts += 1;
      if (installDrawerController() || attempts >= 40) return;
      drawerInstallTimer = window.setTimeout(attempt, 100);
    }
    attempt();
  }

  async function guardCheckout() {
    if (checkoutRunning) return;
    checkoutRunning = true;
    setCartError('');
    setCheckoutBusy(true);
    try {
      await cartEditQueue;
      var result = await reconcileCart();
      renderStableCart(result.cart);
      window.location.assign('/checkout');
    } catch (error) {
      setCartError(errorMessage);
      console.error('HoK cart integrity checkout verification failed.', error);
    } finally {
      checkoutRunning = false;
      setCheckoutBusy(false);
    }
  }

  document.addEventListener('click', function (event) {
    var addButton = event.target.closest('#purchase, .quick-add-button');
    if (addButton) {
      var root = addButton.closest('#product-box, [data-wetheme-section-type="product--featured-product"]');
      if (root) syncProductVariant(root);
    }
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (form && form.matches('form[action*="/cart/add"]')) {
      syncProductVariant(form.closest('#product-box, [data-wetheme-section-type="product--featured-product"]'));
      return;
    }
    var submitter = event.submitter;
    if (form && submitter && submitter.name === 'checkout' && form.matches('#cartSlideoutAside .cart-drawer-form, #cartform')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      guardCheckout();
    }
  }, true);

  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    fetchCart().then(function (cart) {
      if (installDrawerController()) window.wetheme.cartDrawer.updateCartDrawer(cart);
    }).catch(function (error) {
      console.error('HoK cart integrity page restore failed.', error);
    });
  });

  function initialise() {
    window.setTimeout(syncAllProductVariants, 0);
    window.setTimeout(syncAllProductVariants, 250);
    scheduleDrawerInstall();
    if (window.location.pathname === '/cart') {
      reconcileCart().then(function (result) {
        if (result.changed && !sessionStorage.getItem('hok-cart-integrity-reloaded')) {
          sessionStorage.setItem('hok-cart-integrity-reloaded', 'true');
          window.location.reload();
        } else {
          sessionStorage.removeItem('hok-cart-integrity-reloaded');
        }
      }).catch(function () {
        setCartError(errorMessage);
      });
    }
  }

  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(syncAllProductVariants, 0);
    scheduleDrawerInstall();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialise);
  else initialise();
})();


